import { useRef } from 'react';
import { Button, IconButton } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { parsePatDefinitions, readPatFile } from '../../utils/hatchPat';
import { useHatchPatternStore } from '../../state/useHatchPatternStore';
import { notify } from '../../utils/notify';

/**
 * "Import .pat" button for the hatch pattern library. Reads an AutoCAD hatch
 * pattern file (one `*NAME, description` header + definition rows per
 * pattern), stores every parsed definition in the persisted pattern store
 * and makes the names resolvable by HATCH / HATCHEDIT / Filled Regions.
 * Re-importing the same file replaces its entry in place.
 */
export function PatImportButton({ compact = false }: { compact?: boolean }) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportFile = async (file: File) => {
        try {
            const text = await readPatFile(file);
            const patterns = parsePatDefinitions(text);
            if (patterns.length === 0) {
                notify(`.pat import failed: no pattern definitions found in ${file.name}.`, 'error');
                return;
            }
            useHatchPatternStore.getState().importFile({
                fileName: file.name,
                importedAt: Date.now(),
                patterns,
            });
            const dashed = patterns.reduce((acc, p) => acc + (p.lines.some((l) => l.dashes.length > 0) ? 1 : 0), 0);
            notify(
                `.pat imported: ${patterns.length} pattern(s) from ${file.name}${dashed ? `, ${dashed} with dash lines` : ''}. Available in HATCH and Filled Region patterns.`,
                'success'
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error.';
            notify(`.pat import failed: ${message}`, 'error');
        }
    };

    if (compact) {
        return (
            <>
                <IconButton
                    aria-label="Import .pat file"
                    icon={<Upload size={12} />}
                    size="xs"
                    variant="outline"
                    title="Import AutoCAD .pat hatch patterns"
                    onClick={() => fileInputRef.current?.click()}
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pat,.txt,text/plain"
                    className="hidden-file-input"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleImportFile(file);
                        e.target.value = '';
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Button
                size="xs"
                variant="outline"
                leftIcon={<Upload size={12} />}
                w="100%"
                justifyContent="flex-start"
                onClick={() => fileInputRef.current?.click()}
            >
                Import .pat
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".pat,.txt,text/plain"
                className="hidden-file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImportFile(file);
                    e.target.value = '';
                }}
            />
        </>
    );
}
